import { useEffect, useState } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';

/**
 * Custom hook to fetch and apply tenant branding (colors and favicon).
 * Applies branding when user is authenticated and branding feature is enabled.
 */
export const useBranding = () => {
  const { user, tenant, hasFeature, hasEntitlement, featureFlags, entitlements } = useEntitlements();
  const [branding, setBranding] = useState(null);
  
  useEffect(() => {
    // Check if branding is enabled
    const brandingEnabled = hasFeature('enable_white_label') || hasEntitlement('white_label');
    
    if (!user || !tenant || !brandingEnabled) {
      // Reset to defaults if branding disabled
      resetBranding();
      return;
    }
    
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenant, featureFlags, entitlements]);
  
  const fetchBranding = async () => {
    try {
      const response = await api.get('/v1/branding/');
      if (response.data.results && response.data.results.length > 0) {
        const brandingData = response.data.results[0];
        setBranding(brandingData);
        applyBranding(brandingData);
      } else {
        // No branding configured, reset to defaults
        resetBranding();
      }
    } catch (err) {
      console.error('Error fetching branding:', err);
      resetBranding();
    }
  };
  
  const applyBranding = (brandingData) => {
    // Apply colors to CSS variables
    if (brandingData.primary_color) {
      document.documentElement.style.setProperty('--primary-color', brandingData.primary_color);
    }
    if (brandingData.secondary_color) {
      document.documentElement.style.setProperty('--secondary-color', brandingData.secondary_color);
    }
    if (brandingData.accent_color) {
      document.documentElement.style.setProperty('--accent-color', brandingData.accent_color);
    }
    
    // Update favicon
    if (brandingData.favicon_url) {
      updateFavicon(brandingData.favicon_url);
    } else {
      // Reset to default if no favicon configured
      resetFavicon();
    }
    
    // Update title and description
    updateTitleAndDescription(brandingData.company_name);
  };
  
  const updateFavicon = (faviconUrl) => {
    // Remove existing favicon links
    const existingLinks = document.querySelectorAll('link[rel*="icon"]');
    existingLinks.forEach(link => link.remove());
    
    // Determine MIME type based on file extension
    let mimeType = 'image/png';
    if (faviconUrl.endsWith('.ico')) {
      mimeType = 'image/x-icon';
    } else if (faviconUrl.endsWith('.svg')) {
      mimeType = 'image/svg+xml';
    }
    
    // Create new favicon link
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = mimeType;
    link.href = faviconUrl;
    document.head.appendChild(link);
    
    // Also update apple-touch-icon for better mobile support
    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = faviconUrl;
    document.head.appendChild(appleLink);
  };
  
  const resetFavicon = () => {
    // Remove existing favicon links
    const existingLinks = document.querySelectorAll('link[rel*="icon"]');
    existingLinks.forEach(link => link.remove());
    
    // Reset to default favicon from public folder
    const defaultFavicon = `${process.env.PUBLIC_URL || ''}/favicon.svg`;
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.href = defaultFavicon;
    document.head.appendChild(link);
  };
  
  const updateTitleAndDescription = (companyName) => {
    // Update document title
    if (companyName) {
      document.title = `${companyName} Portal`;
    } else {
      document.title = 'Portalr';
    }
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    if (companyName) {
      metaDescription.setAttribute('content', `${companyName} Portal`);
    } else {
      metaDescription.setAttribute('content', 'Portalr - Portalr platform');
    }
  };
  
  const resetTitleAndDescription = () => {
    document.title = 'Portalr';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Portalr - Portalr platform');
    }
  };
  
  const resetBranding = () => {
    // Clear inline overrides so mamba-platform + portalr token bridge apply
    document.documentElement.style.removeProperty('--primary-color');
    document.documentElement.style.removeProperty('--secondary-color');
    document.documentElement.style.removeProperty('--accent-color');
    
    // Reset favicon to default
    resetFavicon();
    
    // Reset title and description to defaults
    resetTitleAndDescription();
  };
  
  return { branding };
};
